/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { UnauthorizedEmptyState } from '@carbon/ibm-products';
import {
  Link,
  RadioButtonGroup,
  RadioButton,
  TextInput
} from 'carbon-components-react';
import cx from 'classnames';
import _ from 'lodash';
import { useState, useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import BetaTag from '@ibm-watson/shared/modules/ia/components/beta-tag/BetaTag';
import { useAppSelector } from '../../../store';
import { ELASTIC_SEARCH_INDEX_INPUT_METHODS } from '../../../ts/skill';
import { generateDocsUrl } from '../../ia/util/docs';
import {
  setElasticIndex,
  setElasticIndexInputMethod,
  setDraftTitleOptionValue,
  setDraftBodyOptionValue,
  setDraftUrlOptionValue,
  setDraftElasticQueryBodyValue,
  setDraftElasticFilterValue
} from '../store/reducer';
import {
  selectIsElserSupported,
  selectIsElserSupportedLoading,
  selectElserModelId,
  selectDraftElasticIndex,
  selectDraftManagedIndex,
  selectDraftTitleOptionValue,
  selectDraftBodyOptionValue,
  selectIsDocumentUploadEnabled
} from '../store/selectors';
import { ELSER_MODELS } from '../util/constants';
import { ElasticSearchIndexConfigMessages as messages } from './messages';

import './ElasticSearchIndexConfig.scss';

interface ElasticSearchIndexConfigProps {
  tabDisplay?: boolean;
  assistantName: string;
  readOnly: boolean;
}
const ELASTICSEARCH_ADD_DOCS_URL =
  generateDocsUrl(
    '/watson-assistant?topic=watson-assistant-search-elasticsearch-add'
  ) + '#setup-elasticsearch';

const ELASTICSEARCH_UPLOAD_DOC =
  generateDocsUrl(
    'watson-assistant?topic=watson-assistant-search-elasticsearch-add'
  ) + '#elasticsearch-upload-documents';

export const ElasticSearchIndexConfig = ({
  tabDisplay = false,
  assistantName,
  readOnly
}: ElasticSearchIndexConfigProps) => {
  const dispatch = useDispatch();
  const { formatMessage: t } = useIntl();
  const index = useAppSelector(selectDraftElasticIndex);
  const managedIndex = useAppSelector(selectDraftManagedIndex);
  const [radioButton, setRadioButton] = useState(
    index !== '' && managedIndex === index && tabDisplay
      ? ELASTIC_SEARCH_INDEX_INPUT_METHODS.UPLOAD
      : ELASTIC_SEARCH_INDEX_INPUT_METHODS.INDEX
  );

  const elserSupported = useAppSelector(selectIsElserSupported);
  const elserSupportedLoading = useAppSelector(selectIsElserSupportedLoading);

  const modelId = useAppSelector(selectElserModelId);
  const isDocumentUploadEnabled = useAppSelector(selectIsDocumentUploadEnabled);

  const title = useAppSelector(selectDraftTitleOptionValue);
  const body = useAppSelector(selectDraftBodyOptionValue);

  const [invalidIndex, setInvalidIndex] = useState(false);
  const [invalidIndexText, setInvalidIndexText] = useState('');

  const manualIndexChange = e => {
    e.stopPropagation();
    e.preventDefault();
    const index = e?.target?.value ?? '';

    if (index === null || _.isEmpty(index?.trim())) {
      setInvalidIndex(true);
      setInvalidIndexText(t(messages.invalidEmptyValueText));
    } else {
      setInvalidIndex(false);
      setInvalidIndexText('');
    }
    dispatch(setElasticIndex(index));
  };

  const determineElserQuery = modelId => {
    if (modelId === ELSER_MODELS.elser_model_1.id) {
      return ELSER_MODELS.elser_model_1.queryBody;
    } else if (modelId === ELSER_MODELS['elser_model_2_linux-x86_64'].id) {
      return ELSER_MODELS['elser_model_2_linux-x86_64'].queryBody;
    } else if (modelId === ELSER_MODELS.elser_model_2.id) {
      return ELSER_MODELS.elser_model_2.queryBody;
    } else {
      return ELSER_MODELS.elser_model_1.queryBody;
    }
  };

  const radioSelection = value => {
    setRadioButton(value);

    if (value === ELASTIC_SEARCH_INDEX_INPUT_METHODS.INDEX) {
      dispatch(
        setElasticIndexInputMethod(ELASTIC_SEARCH_INDEX_INPUT_METHODS.INDEX)
      );
      dispatch(setElasticIndex(''));
      dispatch(setDraftElasticQueryBodyValue(''));
      dispatch(setDraftElasticFilterValue(''));
      dispatch(setDraftTitleOptionValue('title'));
      dispatch(setDraftBodyOptionValue('text'));
      dispatch(setDraftUrlOptionValue(''));
    } else {
      dispatch(
        setElasticIndexInputMethod(ELASTIC_SEARCH_INDEX_INPUT_METHODS.UPLOAD)
      );
      setInvalidIndex(false);
      setInvalidIndexText('');

      if (managedIndex === '') {
        const uploadIndex =
          assistantName
            .replace(/[^0-9a-z-A-Z ]/g, '')
            .replace(/ +/, ' ')
            .toLowerCase()
            .split(' ')
            .join('-') +
          '-' +
          Math.floor(Math.random() * 999) +
          '-' +
          modelId;

        dispatch(setElasticIndex(uploadIndex));
      } else {
        dispatch(setElasticIndex(managedIndex));
      }
      const elserQuery = determineElserQuery(modelId);
      dispatch(setDraftElasticQueryBodyValue(elserQuery));
      dispatch(setDraftTitleOptionValue('title'));
      dispatch(setDraftBodyOptionValue('text'));
      dispatch(setDraftUrlOptionValue(''));
      dispatch(setDraftElasticFilterValue(''));
    }
  };

  useEffect(() => {
    if (title === '') {
      dispatch(setDraftTitleOptionValue('title'));
    }
    if (body === '') {
      dispatch(setDraftBodyOptionValue('text'));
    }
  }, []);

  const titleMessage = tabDisplay ? messages.titleTab : messages.title;
  const descriptionMessage = isDocumentUploadEnabled
    ? messages.description
    : messages.descriptionNoUpload;

  const indexInput = (
    <div className="ElasticSearchIndexConfig__radio-contents">
      <TextInput
        autoComplete="off"
        id="ElasticSearchInstanceConfig__form-index-input"
        invalid={invalidIndex}
        invalidText={invalidIndexText}
        placeholder={t(messages.indexPlaceholder)}
        readOnly={readOnly}
        labelText={t(messages.index)}
        onChange={manualIndexChange}
        size="md"
        value={
          radioButton === ELASTIC_SEARCH_INDEX_INPUT_METHODS.UPLOAD ? '' : index
        }
        disabled={
          isDocumentUploadEnabled
            ? radioButton !== ELASTIC_SEARCH_INDEX_INPUT_METHODS.INDEX
            : false
        }
        type="text"
      />
    </div>
  );
  return (
    <div
      className={cx('ElasticSearchIndexConfig', {
        'ElasticSearchIndexConfig-no-padding': tabDisplay
      })}>
      <>
        <div
          className={cx('ElasticSearchIndexConfig__title', {
            'ElasticSearchIndexConfig__title-tab': tabDisplay
          })}>
          <FormattedMessage {...titleMessage} />
        </div>
        <div
          className={cx('ElasticSearchIndexConfig__description', {
            'ElasticSearchIndexConfig__description-tab': tabDisplay
          })}>
          <FormattedMessage {...descriptionMessage} />
          <Link
            target="_blank"
            rel="noopener noreferrer"
            className="ElasticSearchIndexConfig__link"
            href={ELASTICSEARCH_ADD_DOCS_URL}>
            <FormattedMessage {...messages.learnMore} />
          </Link>
        </div>
      </>

      <div className="ElasticSearchIndexConfig__radio-group">
        {isDocumentUploadEnabled ? (
          <RadioButtonGroup
            name="index-radio-btn-group"
            valueSelected={radioButton}
            onChange={radioSelection}
            disabled={readOnly}>
            <RadioButton
              labelText={t(messages.useIndex)}
              id="index-radio-button"
              value={'index'}
            />
            {indexInput}
            <RadioButton
              labelText={
                <span>
                  {t(messages.uploadDocs)}
                  <BetaTag />
                </span>
              }
              id="upload-radio-button"
              value={'upload'}
              disabled={!elserSupported}
            />
            <div className="ElasticSearchIndexConfig__radio-contents ">
              {!elserSupported && !elserSupportedLoading && (
                <UnauthorizedEmptyState
                  className="ElasticSearchIndexConfig__upgrade"
                  link={{
                    href: ELASTICSEARCH_UPLOAD_DOC,
                    text: (
                      <Link
                        href={ELASTICSEARCH_UPLOAD_DOC}
                        target="_blank"
                        rel="noopener noreferrer">
                        {t(messages.upgradeRequiredButton)}
                      </Link>
                    )
                  }}
                  size="sm"
                  illustrationPosition="bottom"
                  illustrationDescription={t(messages.upgradeRequiredImage)}
                  subtitle={t(messages.upgradeRequiredDescription)}
                  title={t(messages.upgradeRequired)}
                />
              )}
            </div>
          </RadioButtonGroup>
        ) : (
          indexInput
        )}
      </div>
    </div>
  );
};
